import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw, ExternalLink, Info } from 'lucide-react';
import { useApp } from '../hooks/useApps';
import Spinner from '../components/ui/Spinner';
import { isSelfHosted } from '../utils/appSchema';
import { trackEvent } from '../utils/analytics';

/**
 * Chạy ứng dụng nội bộ (thư mục trong `public/`) ngay trong site qua iframe.
 * Chỉ tới được từ nút "Dùng thử ngay" trên trang giới thiệu.
 */
const AppLive = () => {
    const { id } = useParams();
    const { app, loading } = useApp(id);
    const [isFrameLoading, setIsFrameLoading] = useState(true);
    // Đổi key để React gắn lại iframe — cách tải lại đáng tin cậy nhất cho nội dung nhúng.
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        if (app) trackEvent('app_live_open', 'apps', app.title);
    }, [app]);

    if (loading && !app) {
        return (
            <div className="flex justify-center py-32">
                <Spinner size="lg" />
            </div>
        );
    }

    // Ứng dụng chạy ở tên miền khác thì không nhúng được — quay lại trang giới thiệu.
    if (!app || !isSelfHosted(app)) {
        return <Navigate to={app ? `/app/${app.id}` : '/'} replace />;
    }

    const src = `/${app.path}/index.html`;

    const reload = () => {
        setIsFrameLoading(true);
        setReloadKey((key) => key + 1);
    };

    return (
        <div className="relative h-[calc(100vh-4rem)] md:h-[calc(100vh-5rem)] flex flex-col bg-slate-50 dark:bg-slate-950">
            <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-white/70 dark:bg-slate-900/70 backdrop-blur-glass border-b border-slate-200/60 dark:border-white/[0.06]">
                <div className="flex items-center gap-3 min-w-0">
                    <Link
                        to={`/app/${app.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors shrink-0"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span className="hidden sm:inline">Giới thiệu</span>
                    </Link>
                    <span className="text-sm font-semibold text-slate-900 dark:text-white truncate">{app.title}</span>
                </div>

                <div className="flex items-center gap-1 shrink-0">
                    <button
                        onClick={reload}
                        title="Tải lại"
                        className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white transition-colors"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                    <a
                        href={src}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Mở trong tab mới"
                        className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white transition-colors"
                    >
                        <ExternalLink className="w-4 h-4" />
                    </a>
                </div>
            </div>

            <div className="flex-1 relative overflow-hidden">
                {isFrameLoading && (
                    <div className="absolute inset-0 bg-white/90 dark:bg-slate-950/90 backdrop-blur-sm flex items-center justify-center z-10">
                        <div className="text-center">
                            <Spinner size="md" className="mx-auto mb-4" />
                            <p className="text-slate-600 dark:text-slate-300 font-medium">Đang tải {app.title}...</p>
                            <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400 mt-2">
                                <Info className="w-3 h-3" />
                                Ứng dụng đang chạy ngay trong trang này
                            </p>
                        </div>
                    </div>
                )}

                <iframe
                    key={reloadKey}
                    src={src}
                    title={app.title}
                    onLoad={() => setIsFrameLoading(false)}
                    className="w-full h-full border-none"
                />
            </div>
        </div>
    );
};

export default AppLive;
